import { useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import {
  useLazyGetAnswersQuery,
  useLazyGetSectionsQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import SurveyViewFormContentList from "../SurveyViewFormContentList";
import { SurveyViewFormContentProps } from "../../types";
import {
  AnswerResponse,
  SectionResponse,
  SectionStatisticsResponse,
} from "@/redux/survey/types";
import SelectionToolbar from "@/features/survey-views/form/components/SelectionToolbar";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

import "./styles.css";

const SurveyViewFormContent = ({ form }: SurveyViewFormContentProps) => {
  const { t } = useTranslation();

  const [getSections, sectionsResponse] = useLazyGetSectionsQuery();
  const [getSurveyStatistics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [getAnswers] = useLazyGetAnswersQuery();
  const currentUser = useSelector(selectCurrentUser);
  const currentOrganizationId = useOrganizationId();

  const surveyId = useWatch({
    control: form.control,
    name: "survey",
  });
  const currentSelectedAnswers = useWatch({
    control: form.control,
    name: "answers",
  });

  useEffect(() => {
    if (surveyId) {
      getSections({ survey: surveyId, pagination: "off", parentOnly: true });
      getSurveyStatistics({ survey: surveyId });
    }
  }, [surveyId]);

  const handleSectionSelect = useCallback(
    async (sectionIds: number[], checked: boolean) => {
      const answers = await getAnswers({
        sections: sectionIds,
        organization: currentOrganizationId,
        pagination: "off",
        mode: "base",
      });

      const answersData = answers.data as AnswerResponse[];
      if (!answersData?.length) return;

      if (checked) {
        const currentAnswerIds = new Set(
          currentSelectedAnswers.map((answer) => answer.answer),
        );

        const newlySelectedAnswers = answersData
          .filter((answer) => !currentAnswerIds.has(answer.id)) // Only add truly new answers
          .map((answer) => ({
            answer: answer.id,
            question: answer.question,
            attachedBlocks: answer.attachedBlocks.map((block) => ({
              id: block.id,
              sharingRights: block.sharingRights,
            })),
          }));

        if (newlySelectedAnswers.length > 0) {
          form.setValue("answers", [
            ...currentSelectedAnswers,
            ...newlySelectedAnswers,
          ]);
        }
      } else {
        const sectionAnswerIds = new Set(
          answersData.map((answer) => answer.id),
        );
        const filteredAnswers = currentSelectedAnswers.filter(
          (answer) => !sectionAnswerIds.has(answer.answer),
        );
        form.setValue("answers", filteredAnswers);
      }
    },
    [getAnswers, currentUser?.organization?.id, currentSelectedAnswers, form],
  );

  const getIsSectionSelected = useCallback(
    (sectionId: number) => {
      if (!sectionsResponse.isSuccess || !surveyStatisticsResponse.isSuccess)
        return false;
      const sectionsData = sectionsResponse.data as SectionResponse[];
      const sectionStatisticsData =
        surveyStatisticsResponse.data as SectionStatisticsResponse[];
      const parentSection = sectionsData.find(
        (section) => section.id === sectionId,
      );
      const sectionStatistics = sectionStatisticsData.find(
        (sectionStatistics) => sectionStatistics.id === sectionId,
      );
      if (!parentSection || !sectionStatistics) return false;

      const sectionQuestionIds = [parentSection, ...parentSection.tabs].reduce<
        number[]
      >((acc, section) => {
        acc = acc.concat(section.questions);
        return acc;
      }, []);
      const selectedQuestions = currentSelectedAnswers.reduce<number[]>(
        (acc, answer) => {
          if (
            sectionQuestionIds.includes(answer.question) &&
            !acc.includes(answer.question)
          ) {
            acc.push(answer.question);
          }
          return acc;
        },
        [],
      );
      const totalAnsweredQuestions =
        sectionStatistics.answeredRequiredQuestionsCount +
        sectionStatistics.answeredOptionalQuestionsCount;
      return (
        totalAnsweredQuestions !== 0 &&
        totalAnsweredQuestions === selectedQuestions.length
      );
    },
    [sectionsResponse, surveyStatisticsResponse, currentSelectedAnswers],
  );

  const sectionItems = useMemo(() => {
    if (!sectionsResponse.isSuccess || !surveyStatisticsResponse.isSuccess)
      return [];

    const sectionsData = sectionsResponse.data as SectionResponse[];
    const sectionStatisticsData =
      surveyStatisticsResponse.data as SectionStatisticsResponse[];
    const sectionsStatistics = sectionStatisticsData.reduce<
      Record<string, SectionStatisticsResponse>
    >((acc, sectionStatistics) => {
      acc[sectionStatistics.id.toString()] = sectionStatistics;
      return acc;
    }, {});

    return sectionsData
      .map((parentSection) => {
        const sectionQuestionIds = [
          parentSection,
          ...parentSection.tabs,
        ].reduce<number[]>((acc, section) => {
          acc = acc.concat(section.questions);
          return acc;
        }, []);
        const sectionStatistics = sectionsStatistics[parentSection.id];
        let answeredQuestionsCount = 0;
        if (sectionStatistics) {
          answeredQuestionsCount =
            sectionStatistics.answeredRequiredQuestionsCount +
            sectionStatistics.answeredOptionalQuestionsCount;
        }
        const selectedQuestions = currentSelectedAnswers.reduce<number[]>(
          (acc, answer) => {
            if (
              sectionQuestionIds.includes(answer.question) &&
              !acc.includes(answer.question)
            ) {
              acc.push(answer.question);
            }
            return acc;
          },
          [],
        );

        return {
          key: `section-${String(parentSection.id)}`,
          label: (
            <BaseCheckbox
              label={`${parentSection.title} ${selectedQuestions.length}/${answeredQuestionsCount}`}
              checked={getIsSectionSelected(parentSection.id)}
              disabled={answeredQuestionsCount === 0}
              onChange={async (checked) =>
                await handleSectionSelect(
                  [
                    parentSection.id,
                    ...parentSection.tabs.map((section) => section.id),
                  ],
                  checked,
                )
              }
            />
          ),
          children: (
            <SurveyViewFormContentList
              form={form}
              sectionIds={[
                parentSection.id,
                ...parentSection.tabs.map((section) => section.id),
              ]}
            />
          ),
        };
      })
      .filter((item) => item != null);
  }, [
    sectionsResponse,
    surveyStatisticsResponse,
    getIsSectionSelected,
    currentSelectedAnswers,
  ]);

  if (surveyStatisticsResponse.isError) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col gap-400 full-height">
      <h2 className="header-s-medium">
        {t("surveyViews.labels.formContentLabel")}
      </h2>
      <h3 className="body-m-regular">
        {t("surveyViews.labels.formContentDescription")}
      </h3>
      <SelectionToolbar form={form} />

      {sectionsResponse.isLoading && <BaseSpinner />}
      {sectionsResponse.isError && <div>Error loading sections</div>}
      <BaseAccordion
        items={sectionItems}
        expandIconPosition="end"
        className="survey-view-accordion"
      />
    </div>
  );
};

export default SurveyViewFormContent;
